import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import useSWR from "swr";
import { fetcher } from "../../../services/fetcher";
import { SalesLeads_Api } from "../../../api/endpoints";
import {
  Button,
  Group,
  Loader,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { errorNotifications } from "../../../utils/handleNotifications";
import {
  LEAD_TYPE_OPTIONS,
  SOURCE_OPTIONS,
  STATUS_OPTIONS,
} from "../utils/leadsOptions";
import {
  buildLeadPayload,
  isLeadFormComplete,
  leadToFormValues,
  planIdFromLead,
  planMeta,
  planTitle,
  unwrapLead,
} from "../utils/leadFormUtils";
import { useLazyAssignPlans } from "../hooks/useLazyAssignPlans";

const parseErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return "An unknown error occurred.";
  if (typeof data === "string") return data;
  if (data.message) return data.message;
  return Object.entries(data)
    .map(([key, value]) =>
      Array.isArray(value) ? `${key}: ${value.join(", ")}` : `${key}: ${value}`
    )
    .join(" | ");
};

const LeadForm = ({
  lead,
  onSubmit,
  onCreateAndQuotation,
  submitLabel,
  showCancel,
  onCancel,
  createMode = false,
}) => {
  const isEdit = Boolean(lead?.id);
  const [form, setForm] = useState(() => leadToFormValues(lead));
  const [activityId, setActivityId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitIntent, setSubmitIntent] = useState("create");
  const [activeTab, setActiveTab] = useState("details");
  const canSendQuotation = isLeadFormComplete(form, activityId);

  const { data: leadDetail } = useSWR(
    isEdit && lead?.id ? `${SalesLeads_Api}${lead.id}/` : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  const editLead = useMemo(() => {
    if (!lead?.id) return lead;
    if (!leadDetail) return lead;
    return { ...lead, ...unwrapLead(leadDetail) };
  }, [lead, leadDetail]);

  const { plans, isPlansLoading, loadPlans, resetPlansLoad } = useLazyAssignPlans();

  useEffect(() => {
    if (isEdit) loadPlans();
  }, [isEdit, lead?.id, loadPlans]);

  useEffect(() => {
    if (!editLead?.id) return;
    setForm(leadToFormValues(editLead));
    setActivityId(planIdFromLead(editLead));
    setErrorMessage("");
  }, [editLead]);

  useEffect(() => {
    if (!editLead?.id || !plans.length) return;
    const id = planIdFromLead(editLead, plans);
    if (id) setActivityId(id);
  }, [editLead, plans]);

  useEffect(() => () => resetPlansLoad(), [resetPlansLoad]);

  useEffect(() => {
    if (!canSendQuotation && activeTab === "quotation") setActiveTab("details");
  }, [activeTab, canSendQuotation]);

  const activityOptions = useMemo(() => {
    const options = plans.map((p) => ({
      value: String(p.id),
      label: planTitle(p),
      meta: planMeta(p) || undefined,
    }));
    const selected = activityId || planIdFromLead(editLead, plans);
    const trek = form.trek?.trim();
    if (selected && !options.some((o) => o.value === selected)) {
      options.unshift({
        value: selected,
        label: trek || `Plan #${selected}`,
      });
    }
    return options;
  }, [plans, activityId, editLead, form.trek]);

  const handleActivityChange = (value) => {
    setActivityId(value || "");
    const plan = plans.find((p) => String(p.id) === value);
    setForm((prev) => ({ ...prev, trek: plan ? planTitle(plan) : "" }));
  };

  const runSubmit = async (intent) => {
    setSubmitIntent(intent);
    setIsSubmitting(true);
    setErrorMessage("");
    const payload = buildLeadPayload(form, activityId);
    try {
      if (intent === "quotation" && onCreateAndQuotation) {
        await onCreateAndQuotation(payload);
      } else {
        await onSubmit(payload);
      }
    } catch (error) {
      const message = parseErrorMessage(error);
      setErrorMessage(message);
      errorNotifications(error?.response?.data || {}, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    runSubmit("create");
  };

  const today = dayjs().startOf("day").toDate();

  const fields = (
    <>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <TextInput
            label="Name"
            placeholder="Full name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            required
            withAsterisk
          />
          <NumberInput
            label="Phone"
            placeholder="10-digit number"
            hideControls
            required
            withAsterisk
            allowDecimal={false}
            allowNegative={false}
            clampBehavior="none"
            value={form.phone}
            onChange={(value) => setForm((prev) => ({ ...prev, phone: value ?? "" }))}
          />
          <TextInput
            label="Email"
            placeholder="customer@email.com"
            value={form.email}
            onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
            required
            withAsterisk
          />
          <Select
            label="Plan Name"
            placeholder="Search plan..."
            data={activityOptions}
            searchable
            required
            withAsterisk
            value={(activityId || planIdFromLead(editLead, plans)) || null}
            onChange={handleActivityChange}
            onDropdownOpen={loadPlans}
            rightSection={isPlansLoading ? <Loader size="xs" /> : null}
            renderOption={({ option }) => (
              <Stack gap={2} py={2}>
                <Text size="sm" lh={1.3}>{option.label}</Text>
                {option.meta ? (
                  <Text size="xs" c="dimmed" lh={1.2}>{option.meta}</Text>
                ) : null}
              </Stack>
            )}
          />
          <DatePickerInput
            label="Plan Start Date"
            placeholder="dd-mm-yyyy"
            valueFormat="DD-MM-YYYY"
            value={form.start_date}
            required={createMode}
            withAsterisk={createMode}
            minDate={isEdit ? undefined : today}
            onChange={(value) =>
              setForm((prev) => ({
                ...prev,
                start_date: value,
                end_date:
                  prev.end_date && value && dayjs(prev.end_date).isBefore(dayjs(value), "day")
                    ? null
                    : prev.end_date,
              }))
            }
          />
          <DatePickerInput
            label="Plan End Date"
            placeholder="dd-mm-yyyy"
            valueFormat="DD-MM-YYYY"
            value={form.end_date}
            required={createMode}
            withAsterisk={createMode}
            minDate={
              form.start_date
                ? dayjs(form.start_date).startOf("day").toDate()
                : isEdit
                  ? undefined
                  : today
            }
            onChange={(value) => setForm((prev) => ({ ...prev, end_date: value }))}
          />
          <NumberInput
            label="Persons"
            min={1}
            value={form.persons}
            onChange={(value) => setForm((prev) => ({ ...prev, persons: value || 1 }))}
          />
          <Select
            label="Source"
            data={SOURCE_OPTIONS}
            value={form.source}
            onChange={(value) => setForm((prev) => ({ ...prev, source: value || "WEBSITE" }))}
          />
          <Select
            label="Status"
            data={STATUS_OPTIONS}
            value={form.status}
            onChange={(value) => setForm((prev) => ({ ...prev, status: value || "NEW" }))}
          />
          <Select
            label="Lead Type"
            withAsterisk
            data={LEAD_TYPE_OPTIONS}
            value={form.lead_type}
            onChange={(value) => setForm((prev) => ({ ...prev, lead_type: value || "B2C" }))}
            allowDeselect={false}
            required
          />
        </SimpleGrid>
        <Textarea
          label="Notes"
          placeholder="Initial inquiry notes..."
          value={form.notes}
          onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          minRows={3}
        />
    </>
  );

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="sm">
        {createMode ? (
          <Tabs
            value={activeTab}
            onChange={(value) => value && setActiveTab(value)}
            keepMounted={false}
          >
            <Tabs.List>
              <Tabs.Tab value="details">Lead details</Tabs.Tab>
              <Tabs.Tab value="quotation" disabled={!canSendQuotation}>
                Send quotation
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="details" pt="md">{fields}</Tabs.Panel>
            <Tabs.Panel value="quotation" pt="md">
              <Stack gap="xs">
                <Text size="sm" fw={600}>Ready to send</Text>
                <Text size="sm" c="dimmed">
                  A quotation will be created for {form.name || "this lead"} and emailed to{" "}
                  {form.email || "the customer"}.
                </Text>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        ) : (
          fields
        )}
        {errorMessage ? <Text c="red" size="sm">{errorMessage}</Text> : null}
        <Group justify="flex-end" mt="sm">
          {showCancel ? (
            <Button variant="default" type="button" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          ) : null}
          {createMode ? (
            <>
              <Button
                type="submit"
                loading={isSubmitting && submitIntent === "create"}
                disabled={isSubmitting && submitIntent !== "create"}
              >
                Create Lead
              </Button>
              <Button
                type="button"
                color="blue"
                disabled={!canSendQuotation || isSubmitting}
                loading={isSubmitting && submitIntent === "quotation"}
                onClick={() => runSubmit("quotation")}
              >
                Create Lead and Send Quotation
              </Button>
            </>
          ) : (
            <Button type="submit" loading={isSubmitting}>
              {submitLabel}
            </Button>
          )}
        </Group>
      </Stack>
    </form>
  );
};

export default LeadForm;
